import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { TrackingOrder } from '@jbhi-fi/apis/src/orderTrackingThemeApi/types';
import { OrderTrackingThemeApiClient } from '@jbhi-fi/apis/src/orderTrackingThemeApi/OrderTrackingThemeApiClient';
import { OrderShipmentTransformer } from '../../OrderTracking/transformers/OrderShipmentTransformer';
import { useRecaptchaContext } from '../../context/RecaptchaProvider';

const RECAPTCHA_ACTION = 'orderTracking';

export type UseOrderTrackingResult = {
  order?: TrackingOrder;
  isLoading: boolean;
  statusCode?: number;
  error?: unknown;
};

type UseOrderTrackingProps = { orderTrackingThemeApiClient?: OrderTrackingThemeApiClient } & (
  | { orderId: string }
  | { orderNumber: string; mobileNumber: string }
);

export const useOrderTracking = ({
  orderTrackingThemeApiClient: orderTrackingThemeApiClientProp,
  ...props
}: UseOrderTrackingProps): UseOrderTrackingResult => {
  const { orderTrackingThemeApiClient: orderTrackingThemeApiClientContext } = usePublicApiClient();
  const orderTrackingThemeApiClient = orderTrackingThemeApiClientProp || orderTrackingThemeApiClientContext;
  if (!orderTrackingThemeApiClient) {
    throw new Error('useOrderTracking must be used with a orderTrackingThemeApiClient');
  }

  const { generateToken, isReady } = useRecaptchaContext();

  const handleFetchOrders = async () => {
    const recaptchaToken = (await generateToken?.(RECAPTCHA_ACTION)) ?? '';

    if ('orderId' in props) {
      return orderTrackingThemeApiClient?.getVerifiedTrackingInformation({
        orderId: props.orderId,
        recaptchaToken,
        recaptchaAction: RECAPTCHA_ACTION,
      });
    }
    if ('orderNumber' in props) {
      return orderTrackingThemeApiClient?.getTrackingInformation({
        orderNumber: props.orderNumber,
        phoneNumber: props.mobileNumber,
        recaptchaToken,
        recaptchaAction: RECAPTCHA_ACTION,
      });
    }
  };

  const { data, status, error } = useQuery({
    ...('orderId' in props
      ? { enabled: isReady && Boolean(props.orderId), queryKey: ['useOrderTracking', `orderId: ${props.orderId}`] }
      : 'orderNumber' in props
      ? {
          enabled: isReady && Boolean(props.orderNumber && props.mobileNumber),
          queryKey: ['useOrderTracking', `orderNumber: ${props.orderNumber}`, `mobileNumber: ${props.mobileNumber}`],
        }
      : { enabled: false }),
    queryFn: handleFetchOrders,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  //@ts-ignore
  const isLoading = status === 'loading' || (!isReady && (props?.orderId || props?.orderNumber));
  const order = data?.data ? OrderShipmentTransformer.parseContentSections(data.data) : undefined;

  return { isLoading, order, statusCode: data?.statusCode, error: data?.error?.message || error };
};
